import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import axios from 'axios'
import type {CancelTokenSource} from 'axios'
import {Button} from '../../../../_gori/partials/widgets'
import RequestBase from '../../../api/RequestBase'
import {toAbsoluteUrl} from '../../../../_gori/helpers'
import {convertUnit} from '../../../../_gori/helpers/ConvertHelpers'
import {ROUTES} from '../../../../_gori/constants/_routes'
import {RateCard} from './RateCard'
import {SampleRates} from './SampleRates'
import {
  ShippingRate,
  CalculatorRequest,
  CALCULATOR_CARRIERS,
  WEIGHT_UNITS,
  isValidZip,
  isPositiveNumber,
  handleZipChange,
  normalizeZip,
  numericOnly,
} from '../core/shippingCalculatorHelpers'

const PKG_TYPES = [
  {value: 'custom' as const, labelId: 'CUSTOM_PACKAGE', image: 'custom-package.webp'},
  {value: 'flat' as const, labelId: 'USPS_FLAT_RATES', image: 'flat-package.webp'},
]
const DIMS = ['length', 'width', 'height'] as const
const MAX_DIMENSION = 100

type DimKey = typeof DIMS[number]
type FieldKey = DimKey | 'ship_from' | 'ship_to' | 'weight'
type ErrorState = {message: string; fields: Set<FieldKey>} | null

export function ShippingCostCalculator() {
  const navigate = useNavigate()
  const intl = useIntl()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [shippingFrom, setShippingFrom] = useState('')
  const [shippingTo, setShippingTo] = useState('')
  const [packageType, setPackageType] = useState<'custom' | 'flat'>('custom')
  const [dims, setDims] = useState({length: '', width: '', height: ''})
  const [weight, setWeight] = useState('')
  const [weightUnit, setWeightUnit] = useState<typeof WEIGHT_UNITS[number]>('oz')
  const [showUnitMenu, setShowUnitMenu] = useState(false)
  const [rates, setRates] = useState<ShippingRate[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorState, setErrorState] = useState<ErrorState>(null)

  const cancelSourcesRef = useRef<CancelTokenSource[]>([])
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!showUnitMenu) return
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node))
        setShowUnitMenu(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showUnitMenu])

  useEffect(() => {
    return () => {
      cancelSourcesRef.current.forEach((s) => s.cancel())
      cancelSourcesRef.current = []
    }
  }, [])

  const onCreateClick = useCallback(() => navigate(ROUTES.AUTH.BASE), [navigate])

  const calculate = async () => {
    setRates([])
    setErrorState(null)
    setHasSearched(false)

    const missingFields = new Set<FieldKey>()
    if (!isValidZip(shippingFrom)) missingFields.add('ship_from')
    if (!isValidZip(shippingTo)) missingFields.add('ship_to')
    if (packageType === 'custom') {
      DIMS.forEach((d) => {
        if (!isPositiveNumber(dims[d])) missingFields.add(d)
      })
      if (!isPositiveNumber(weight)) missingFields.add('weight')
    }
    if (missingFields.size > 0) {
      return setErrorState({
        message: intl.formatMessage({id: 'ERROR_FILL_REQUIRED_FIELDS'}),
        fields: missingFields,
      })
    }

    if (packageType === 'custom') {
      const oversizedDims = DIMS.filter((d) => parseFloat(dims[d]) > MAX_DIMENSION)
      if (oversizedDims.length > 0)
        return setErrorState({
          message: intl.formatMessage({id: 'ERROR_INVALID_DIMENSION'}),
          fields: new Set<FieldKey>(oversizedDims),
        })
    }

    cancelSourcesRef.current.forEach((s) => s.cancel())
    cancelSourcesRef.current = []
    const myRequestId = ++requestIdRef.current

    setLoading(true)
    setErrorState(null)

    const baseReq: CalculatorRequest = {
      ship_from: normalizeZip(shippingFrom),
      ship_to: normalizeZip(shippingTo),
      package_type: packageType,
    }
    if (packageType === 'custom') {
      const w = convertUnit({value: parseFloat(weight), unit: weightUnit}, {unit: 'oz'})
      if (!w || w <= 0) {
        setLoading(false)
        return setErrorState({
          message: intl.formatMessage({id: 'ERROR_FILL_REQUIRED_FIELDS'}),
          fields: new Set<FieldKey>(['weight']),
        })
      }
      baseReq.dimensions = {
        length: parseFloat(dims.length),
        width: parseFloat(dims.width),
        height: parseFloat(dims.height),
      }
      baseReq.weight = w
    }

    const carriersToFetch: readonly string[] =
      packageType === 'flat' ? ['usps'] : CALCULATOR_CARRIERS

    const accumulated: ShippingRate[] = []

    await Promise.allSettled(
      carriersToFetch.map(async (carrier) => {
        const source = axios.CancelToken.source()
        cancelSourcesRef.current.push(source)
        try {
          const res: ShippingRate[] = await RequestBase.post(
            '/tools/shipping-calculator',
            {...baseReq, carrier},
            {cancelToken: source.token}
          )
          // Drop stale responses from a superseded click.
          if (requestIdRef.current !== myRequestId) return
          if (Array.isArray(res) && res.length) {
            accumulated.push(...res)
            accumulated.sort((a, b) => a.fees - b.fees)
            setRates([...accumulated])
          }
        } catch (err) {
          if (axios.isCancel(err)) return
        }
      })
    )

    // Only the latest click is allowed to flip the loading / error UI state.
    if (requestIdRef.current !== myRequestId) return
    setHasSearched(true)
    // No rates: do NOT show the in-form red error module — the right column
    // renders the dedicated Figma empty-state illustration + copy instead.
    setLoading(false)
  }

  const zipField = (
    labelId: string,
    value: string,
    set: (v: string) => void,
    id: string,
    isError: boolean
  ) => (
    <div className='d-flex flex-column align-items-start gap-3 flex-fill'>
      <label htmlFor={id} className='shipping-calc__form-label fw-bolder fs-4'>
        {intl.formatMessage({id: labelId})}
      </label>
      <input
        id={id}
        type='text'
        inputMode='numeric'
        maxLength={10}
        className={`form-control bg-white${isError ? ' shipping-calc__zip-input--error' : ''}`}
        placeholder={intl.formatMessage({id: 'ZIP_CODE'})}
        aria-label={intl.formatMessage({id: labelId})}
        aria-invalid={isError ? true : undefined}
        value={value}
        onChange={(e) => {
          set(handleZipChange(e.target.value))
          if (errorState) setErrorState(null)
        }}
      />
    </div>
  )

  const rateGroup = (labelId: string, items: ShippingRate[]) =>
    items.length > 0 && (
      <div className='d-flex flex-column align-self-stretch gap-3'>
        <div className='fs-2 color-blue-dark fw-bolder lh-base'>
          {intl.formatMessage({id: labelId})}
        </div>
        {items.map((r) => (
          <RateCard
            key={`${r.carrier}_${r.service}`}
            rate={r}
            shippingFrom={shippingFrom}
            shippingTo={shippingTo}
            onCreateClick={onCreateClick}
          />
        ))}
      </div>
    )

  return (
    <div className='d-flex flex-column align-items-center gap-6 w-100'>
      <div className='d-flex flex-wrap gap-18 align-items-start justify-content-center shipping-calc__layout'>
        <form
          className='shipping-calc__form shipping-calc__w100-600 bg-white d-flex flex-column align-items-start gap-6 p-6 rounded-2'
          onSubmit={(e) => {
            e.preventDefault()
            calculate()
          }}
          aria-label={intl.formatMessage({id: 'CALCULATE_YOUR_SHIPPING_COSTS'})}
        >
          <div className='d-flex align-items-start gap-6 align-self-stretch'>
            {zipField(
              'SHIP_FROM',
              shippingFrom,
              setShippingFrom,
              'calc-ship-from',
              !!errorState?.fields.has('ship_from')
            )}
            {zipField(
              'SHIP_TO',
              shippingTo,
              setShippingTo,
              'calc-ship-to',
              !!errorState?.fields.has('ship_to')
            )}
          </div>

          <div className='shipping-calc__package-section shipping-calc__w100-600 d-flex flex-column align-self-stretch'>
            <div className='fw-bolder fs-4 lh-base mb-4'>
              {intl.formatMessage({id: 'PACKAGE_TYPE'})}
            </div>
            <div className='d-flex justify-content-center align-self-stretch gap-6'>
              {PKG_TYPES.map((pkg) => (
                <div
                  key={pkg.value}
                  className='cursor-pointer d-flex flex-column align-items-center gap-4'
                  onClick={() => {
                    setPackageType(pkg.value)
                    setErrorState(null)
                    if (pkg.value === 'flat') {
                      setDims({length: '', width: '', height: ''})
                      setWeight('')
                    }
                  }}
                >
                  <div
                    className={`shipping-calc__pkg-img-container bg-white d-flex align-items-center justify-content-center flex-shrink-0 rounded-2 ${
                      packageType !== pkg.value
                        ? 'shipping-calc__pkg-img-container--unselected'
                        : ''
                    }`}
                  >
                    <img
                      src={toAbsoluteUrl(`/media/gori/resources/${pkg.image}`)}
                      alt={intl.formatMessage({id: pkg.labelId})}
                      className='shipping-calc__pkg-img object-fit-cover flex-shrink-0 rounded-1'
                    />
                  </div>
                  <div className='color-blue-dark fw-bolder fs-4 lh-base'>
                    {intl.formatMessage({id: pkg.labelId})}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {packageType === 'custom' && (
            <div className='shipping-calc__dimensions-container py-3 px-3 d-flex flex-column align-self-stretch rounded-2'>
              <div className='shipping-calc__dims-weight-row d-flex align-self-stretch gap-7'>
                <div className='flex-grow-1 d-flex flex-column gap-1'>
                  <div className='color-blue-dark fw-bolder fs-4 lh-base'>
                    {intl.formatMessage({id: 'DIMENSIONS'})}
                  </div>
                  <div className='d-flex gap-2'>
                    {DIMS.map((d) => (
                      <div key={d} className='position-relative flex-grow-1'>
                        <span className='shipping-calc__dim-label fs-7 fw-bold' aria-hidden='true'>
                          {d[0].toUpperCase()}
                        </span>
                        <input
                          type='text'
                          inputMode='decimal'
                          value={dims[d]}
                          className={`shipping-calc__dim-input bg-white shipping-calc__border-radius-6${
                            errorState?.fields.has(d) ? ' shipping-calc__dim-input--error' : ''
                          }`}
                          aria-label={`${intl.formatMessage({id: 'DIMENSIONS'})} ${d}`}
                          aria-invalid={errorState?.fields.has(d) ? true : undefined}
                          onChange={(e) => {
                            if (numericOnly(e.target.value)) {
                              setDims((p) => ({...p, [d]: e.target.value}))
                              if (errorState) setErrorState(null)
                            }
                          }}
                        />
                        <span className='shipping-calc__dim-unit fs-7 fw-bold' aria-hidden='true'>
                          {intl.formatMessage({id: 'IN'})}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className='d-flex flex-column gap-1'>
                  <div className='d-flex align-items-center gap-1'>
                    <div className='flex-grow-1 color-blue-dark fw-bolder fs-4 lh-base'>
                      {intl.formatMessage({id: 'WEIGHT'})}
                    </div>
                    <div
                      ref={dropdownRef}
                      className='cursor-pointer position-relative d-flex align-items-center'
                      onClick={() => setShowUnitMenu(!showUnitMenu)}
                    >
                      <span className='shipping-calc__color-teal fs-7 fw-bold'>{weightUnit}</span>
                      <i className='fa fa-chevron-down ms-1 shipping-calc__color-teal fs-8' />
                      {showUnitMenu && (
                        <div className='shipping-calc__weight-dropdown bg-white shipping-calc__border-radius-6'>
                          {WEIGHT_UNITS.map((u) => (
                            <div
                              key={u}
                              className='cursor-pointer shipping-calc__color-teal py-3 px-4 fs-7'
                              onClick={() => {
                                setWeightUnit(u)
                                setShowUnitMenu(false)
                              }}
                            >
                              {intl.formatMessage({id: u.toUpperCase()})}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <input
                    type='text'
                    inputMode='decimal'
                    value={weight}
                    className={`shipping-calc__weight-input py-0 px-4 shipping-calc__border-radius-6${
                      errorState?.fields.has('weight') ? ' shipping-calc__weight-input--error' : ''
                    }`}
                    aria-label={intl.formatMessage({id: 'WEIGHT'})}
                    aria-invalid={errorState?.fields.has('weight') ? true : undefined}
                    onChange={(e) => {
                      if (numericOnly(e.target.value)) {
                        setWeight(e.target.value)
                        if (errorState) setErrorState(null)
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <Button
            classNameDiv='w-100'
            className='shipping-calc__calculate-btn w-100 fs-2 border-0 text-white d-flex justify-content-center align-items-center'
            submit
            loading={loading}
            label={intl.formatMessage({id: 'SHOW_DISCOUNTED_RATES'})}
            loadingText={intl.formatMessage({id: 'CALCULATING'})}
          />

          {errorState && (
            <div className='shipping-calc__error-module w-100' role='alert'>
              {errorState.message}
            </div>
          )}
        </form>

        {rates.length > 0 ? (
          <div
            className='shipping-calc__results shipping-calc__w100-480 d-flex flex-column align-items-start gap-5 overflow-auto'
            aria-live='polite'
          >
            {rateGroup('CHEAPEST', rates.slice(0, 1))}
            {rateGroup('MORE_RATES', rates.slice(1))}
          </div>
        ) : hasSearched ? (
          <div
            className='shipping-calc__empty-state shipping-calc__w100-480 d-flex flex-column align-items-center justify-content-center'
            aria-live='polite'
            role='status'
          >
            <div className='shipping-calc__empty-state-text'>
              {intl.formatMessage({id: 'NO_SERVICES_AVAILABLE_FOR_DIMENSIONS'})}
            </div>
            <img
              src={toAbsoluteUrl('/media/gori/resources/no-services.svg')}
              alt=''
              aria-hidden='true'
              className='shipping-calc__empty-state-img'
            />
          </div>
        ) : (
          <SampleRates />
        )}
      </div>
    </div>
  )
}
